import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button, Form, Row, Col, Alert, ListGroup, Badge, Toast, ToastContainer } from 'react-bootstrap';
import ElegantModal from '../../components/ElegantModal/ElegantModal';
import { urlApiProd } from '../utiles';
import { sumPesoGrossByMergeGroups, sumPesoNetByMergeGroups } from './invoiceWeightUtils';
import {
  buildInvoicePtcMerchandise,
  packingListTotals,
} from './invoicePtcMerchandise';
import { packingListsSinInvoice } from './packingListSinInvoiceUtils';
import InvoicePtcPreviewModal from './InvoicePtcPreviewModal';
import {
  createInvoicePtcPdfBlobUrl,
  generateInvoicePtcPdf,
  invoicePtcParamsFromFormAndDetail,
  invoicePtcParamsFromFormWithPackingList,
  lineasPdfTienenPrecios,
  proformaFromLista,
} from './generateInvoicePtcPDF';
import {
  createInvoicePtbPdfBlobUrl,
  generateInvoicePtbPdf,
  invoicePtbParamsFromFormAndDetail,
  invoicePtbParamsFromFormWithPackingList,
} from './generateInvoicePtbPDF';
import {
  invoiceNumberCorrelativo,
  numeroParaVistaPrevia,
} from './invoiceNumberUtils';

function foldBusquedaTexto(s) {
  return String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '');
}

function proformaEtiqueta(pi) {
  if (!pi) return '';
  return (pi.id_pi && String(pi.id_pi).trim()) || `PI #${pi.id_proforma}`;
}

function proformaBusquedaHaystack(pi) {
  if (!pi) return '';
  const lbl = proformaEtiqueta(pi);
  const idn = pi.id_proforma != null ? String(pi.id_proforma) : '';
  return `${lbl} ${idn}`.trim();
}

function ultimaProformaId(ids) {
  if (!ids?.length) return '';
  return String(ids[ids.length - 1]);
}

function appendProformaIdOrdenado(prevIds, id) {
  const s = String(id);
  const next = (prevIds || []).filter((x) => String(x) !== s);
  next.push(s);
  return next;
}

function getPiLabel(idProforma, proformasLista) {
  if (!idProforma) return '';
  const pi = proformasLista.find((p) => String(p.id_proforma) === String(idProforma));
  return proformaEtiqueta(pi);
}

function puertoNombreCorto(etiqueta) {
  const t = String(etiqueta || '').trim();
  const idx = t.indexOf(' (');
  return idx >= 0 ? t.slice(0, idx).trim() : t;
}

function buildClienteDireccionTexto(cli, snapshot) {
  if (snapshot && String(snapshot).trim()) return String(snapshot).trim();
  if (!cli) return '';
  const parts = [];
  if (cli.direccion) parts.push(cli.direccion);
  if (cli.ciudad) parts.push(cli.ciudad);
  if (cli.codigo_postal) parts.push(`PIN CODE: ${String(cli.codigo_postal).trim()}`);
  if (cli.pais) parts.push(cli.pais);
  return parts.join('\n');
}

function incotermDesdePi(proforma, incoterms, puertos) {
  if (!proforma) return '';
  const inco = incoterms.find((i) => String(i.id_incoterm) === String(proforma.incoterm));
  const pto = puertos.find((p) => String(p.id_puerto) === String(proforma.puerto));
  const nombreInco = inco?.nombre || '';
  const ptoTxt = pto ? `${pto.nombre}, ${pto.pais || ''}`.trim() : '';
  return [nombreInco, ptoTxt].filter(Boolean).join(' ').trim();
}

function paymentDesdePi(proforma, paymentTerms) {
  if (!proforma?.payment_terms) return '';
  const pt = paymentTerms.find(
    (p) => String(p.id_paymentterm) === String(proforma.payment_terms)
  );
  return pt?.nombre || '';
}

const EMPRESA_PTC = 0;
const EMPRESA_PTB = 1;

/**
 * Formulario Invoice (mismo shell que Crear packing list).
 * PTC: emite PDF PTC + PDF PTB. PTB: solo PDF PTB con reglas de campos PTC.
 */
const fechaHoy = () => {
  const t = new Date();
  const y = t.getFullYear();
  const m = String(t.getMonth() + 1).padStart(2, '0');
  const d = String(t.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Menú autocomplete fuera del modal (evita recorte por overflow del panel). */
function BookingAutocompletePortal({ open, anchorRef, children }) {
  const [menuStyle, setMenuStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setMenuStyle(null);
      return undefined;
    }
    const update = () => {
      const r = anchorRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: r.bottom + 2,
        left: r.left,
        width: r.width,
        zIndex: 10060,
        maxHeight: 220,
      });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);

  if (!open || !menuStyle) return null;
  return createPortal(
    <ListGroup className="shadow border rounded overflow-auto bg-white" style={menuStyle}>
      {children}
    </ListGroup>,
    document.body
  );
}

const AgregarInvoice = ({
  show,
  cerrar,
  invoiceEditId = null,
  packingListDisponibles = [],
  onGuardarExito,
}) => {
  const isEditMode = invoiceEditId != null && invoiceEditId !== '';
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [puertos, setPuertos] = useState([]);
  const [idsProformaSeleccion, setIdsProformaSeleccion] = useState([]);
  const [proformaBusqueda, setProformaBusqueda] = useState('');
  const [proformaMenuOpen, setProformaMenuOpen] = useState(false);
  const proformaInputRef = useRef(null);
  const proformaElegidaRecienteRef = useRef(false);

  const [empresaEmisor, setEmpresaEmisor] = useState(EMPRESA_PTC);
  const [idPackingList, setIdPackingList] = useState(null);
  const [bookingBusqueda, setBookingBusqueda] = useState('');
  const [bookingMenuOpen, setBookingMenuOpen] = useState(false);
  const [packingDisponiblesLocal, setPackingDisponiblesLocal] = useState([]);
  const bookingAnchorRef = useRef(null);

  const [fechaInvoice, setFechaInvoice] = useState(fechaHoy());
  const [fechaEmbarque, setFechaEmbarque] = useState('');
  const [valorUsd, setValorUsd] = useState('');
  const [totalContenedores, setTotalContenedores] = useState('');
  const [totalM3, setTotalM3] = useState('');
  const [hsCodeSnapshot, setHsCodeSnapshot] = useState('');
  const [productDescriptionSnapshot, setProductDescriptionSnapshot] = useState('');
  const [idCliente, setIdCliente] = useState('');
  const [clienteBusqueda, setClienteBusqueda] = useState('');
  const [clienteMenuOpen, setClienteMenuOpen] = useState(false);
  const [clienteDireccion, setClienteDireccion] = useState('');
  const [taxId, setTaxId] = useState('');
  const [pinCode, setPinCode] = useState('');

  const [paisOrigen, setPaisOrigen] = useState('');
  const [paisDestino, setPaisDestino] = useState('');
  const [nombreNave, setNombreNave] = useState('');
  const [puertoOrigen, setPuertoOrigen] = useState('');
  const [puertoDestino, setPuertoDestino] = useState('');
  const [lugarEntrega, setLugarEntrega] = useState('');
  const [nombreBl, setNombreBl] = useState('');
  const [contractNo, setContractNo] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [netWeight, setNetWeight] = useState('');
  const [grossWeight, setGrossWeight] = useState('');
  const [salesTerms, setSalesTerms] = useState('AFIRME');
  const [incotermsTexto, setIncotermsTexto] = useState('');
  const [paymentTermsPtc, setPaymentTermsPtc] = useState('');
  const [quantity, setQuantity] = useState('');
  const [invoiceCurrency, setInvoiceCurrency] = useState('USD');

  const [contractNoPtb, setContractNoPtb] = useState('');
  const [purchaseOrderPtb, setPurchaseOrderPtb] = useState('PTB-01');
  const [termsOfSalePtb, setTermsOfSalePtb] = useState('AFIRME');
  const [clausePtb, setClausePtb] = useState('');
  const [paymentTermsPtb, setPaymentTermsPtb] = useState('WIRE TRANSFER ADVANCE 100%');

  const [numeroIdentificadorPtc, setNumeroIdentificadorPtc] = useState('');
  const [numeroIdentificadorPtb, setNumeroIdentificadorPtb] = useState('');
  const [estadoInvoice, setEstadoInvoice] = useState('prefactura');

  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showGuardadoToast, setShowGuardadoToast] = useState(false);
  const [guardadoToastMensaje, setGuardadoToastMensaje] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrls, setPreviewUrls] = useState({ PTC: null, PTB: null });
  const [previewVariant, setPreviewVariant] = useState('PTC');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const previewParamsRef = useRef(null);
  /** Evita recargar catálogo + invoice en bucle al hidratar el formulario. */
  const invoiceModalLoadKeyRef = useRef(null);
  const hydratingEditRef = useRef(false);

  const bookingElegidoRecienteRef = useRef(false);
  const detalleLineasRef = useRef([]);
  const idProformaUltimaRef = useRef(null);
  const mercPayloadRef = useRef(null);

  const textoEmpresaPtc = 'PALM TRADE CHILE SPA';
  const textoEmpresaPtb = 'PALM TRADE BRASIL LTDA';

  const revokePreviewUrls = useCallback((urls) => {
    if (!urls) return;
    Object.values(urls).forEach((u) => {
      if (u) URL.revokeObjectURL(u);
    });
  }, []);

  const packingDisponiblesLista = useMemo(() => {
    if (packingDisponiblesLocal.length) return packingDisponiblesLocal;
    return packingListDisponibles || [];
  }, [packingDisponiblesLocal, packingListDisponibles]);

  const bookingsOpciones = useMemo(() => {
    const qf = foldBusquedaTexto(bookingBusqueda);
    const base = packingDisponiblesLista.filter((r) => r.nro_booking);
    if (!qf) return base.slice(0, 80);
    return base
      .filter((r) => foldBusquedaTexto(r.nro_booking).includes(qf))
      .slice(0, 80);
  }, [packingDisponiblesLista, bookingBusqueda]);

  const proformasFiltradas = useMemo(() => {
    if (!idCliente) return proformas;
    return proformas.filter(
      (p) => p.cliente != null && String(p.cliente) === String(idCliente)
    );
  }, [proformas, idCliente]);

  const idProformaUltima = useMemo(
    () => ultimaProformaId(idsProformaSeleccion),
    [idsProformaSeleccion]
  );

  const proformasOrdenadas = useMemo(
    () =>
      [...proformasFiltradas].sort((a, b) =>
        proformaEtiqueta(a).localeCompare(proformaEtiqueta(b), 'es', { sensitivity: 'base' })
      ),
    [proformasFiltradas]
  );

  const proformasBusquedaLista = useMemo(() => {
    const yaSeleccion = new Set((idsProformaSeleccion || []).map((id) => String(id)));
    const candidatas = proformasOrdenadas.filter(
      (pi) => !yaSeleccion.has(String(pi.id_proforma))
    );
    const qf = foldBusquedaTexto(proformaBusqueda);
    if (!qf) return candidatas.slice(0, 80);
    return candidatas
      .filter((pi) => foldBusquedaTexto(proformaBusquedaHaystack(pi)).includes(qf))
      .slice(0, 80);
  }, [proformasOrdenadas, proformaBusqueda, idsProformaSeleccion]);

  const clientesFiltrados = useMemo(() => {
    const qf = foldBusquedaTexto(clienteBusqueda);
    const sorted = [...clientes].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
    );
    if (!qf) return sorted.slice(0, 80);
    return sorted
      .filter((c) => foldBusquedaTexto(c.nombre || '').includes(qf))
      .slice(0, 80);
  }, [clientes, clienteBusqueda]);

  const resetForm = useCallback(() => {
    setEmpresaEmisor(EMPRESA_PTC);
    setFechaInvoice(fechaHoy());
    setValorUsd('');
    setTotalContenedores('');
    setTotalM3('');
    setHsCodeSnapshot('');
    setProductDescriptionSnapshot('');
    setIdPackingList(null);
    setBookingBusqueda('');
    setBookingMenuOpen(false);
    setFechaEmbarque('');
    setIdCliente('');
    setClienteBusqueda('');
    setClienteMenuOpen(false);
    setClienteDireccion('');
    setTaxId('');
    setPinCode('');
    setPaisOrigen('');
    setPaisDestino('');
    setNombreNave('');
    setPuertoOrigen('');
    setPuertoDestino('');
    setLugarEntrega('');
    setNombreBl('');
    setContractNo('');
    setIdsProformaSeleccion([]);
    setProformaBusqueda('');
    setProformaMenuOpen(false);
    setPurchaseOrder('');
    setNetWeight('');
    setGrossWeight('');
    setSalesTerms('AFIRME');
    setIncotermsTexto('');
    setPaymentTermsPtc('');
    setQuantity('');
    setInvoiceCurrency('USD');
    detalleLineasRef.current = [];
    idProformaUltimaRef.current = null;
    mercPayloadRef.current = null;
    previewParamsRef.current = null;
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant('PTC');
    setShowPreview(false);
    setPreviewError('');
    setContractNoPtb('');
    setPurchaseOrderPtb('PTB-01');
    setTermsOfSalePtb('AFIRME');
    setClausePtb('');
    setPaymentTermsPtb('WIRE TRANSFER ADVANCE 100%');
    setNumeroIdentificadorPtc('');
    setNumeroIdentificadorPtb('');
    setEstadoInvoice('prefactura');
    setErrors({});
    setSaveError('');
  }, [revokePreviewUrls]);

  const aplicarMercanciaDesdePl = useCallback(
    (pl, pi, numeroInv) => {
      const lineas = pl?.lineas || [];
      detalleLineasRef.current = lineas;
      idProformaUltimaRef.current = pi || null;
      const totals = packingListTotals(lineas);
      const merc = buildInvoicePtcMerchandise(lineas, pi, {
        currency: invoiceCurrency,
        fechaInvoice: fechaInvoice || pl?.fecha_embarque,
        fechaEmbarque: pl?.fecha_embarque || fechaEmbarque,
        numeroInvoice: numeroInv ?? numeroIdentificadorPtc,
        productDescriptionSnapshot:
          pl?.product_description_snapshot || productDescriptionSnapshot,
      });
      mercPayloadRef.current = {
        lineasMercancia: merc.lineasMercancia,
        totalUsd: merc.totalUsd,
        totalBundles: totals.totalBundles,
        totalM3: totals.totalM3,
      };
      if (merc.totalUsd > 0) setValorUsd(String(merc.totalUsd));
      const m3Txt = totals.totalM3 > 0 ? String(totals.totalM3) : '';
      if (m3Txt) setQuantity((prev) => (String(prev).trim() ? prev : m3Txt));
    },
    [
      invoiceCurrency,
      fechaInvoice,
      fechaEmbarque,
      numeroIdentificadorPtc,
      productDescriptionSnapshot,
    ]
  );

  const aplicarDatosFormulario = useCallback((f, inv) => {
    const datos = f || {};
    if (inv?.empresa_emisor != null && inv.empresa_emisor !== '') {
      setEmpresaEmisor(Number(inv.empresa_emisor));
    } else if (datos.empresaEmisor != null && datos.empresaEmisor !== '') {
      setEmpresaEmisor(Number(datos.empresaEmisor));
    }
    if (datos.fechaInvoice) setFechaInvoice(String(datos.fechaInvoice).slice(0, 10));
    else if (inv?.fecha_invoice) setFechaInvoice(String(inv.fecha_invoice).slice(0, 10));
    if (datos.fechaEmbarque) setFechaEmbarque(String(datos.fechaEmbarque).slice(0, 10));
    if (datos.clienteNombre != null) setClienteBusqueda(datos.clienteNombre);
    else if (inv?.nombre_cliente) setClienteBusqueda(inv.nombre_cliente);
    if (datos.clienteDireccion != null) setClienteDireccion(datos.clienteDireccion);
    if (datos.taxId != null) setTaxId(datos.taxId);
    if (datos.pinCode != null) setPinCode(datos.pinCode);
    if (datos.paisOrigen != null) setPaisOrigen(datos.paisOrigen);
    if (datos.paisDestino != null) setPaisDestino(datos.paisDestino);
    else if (inv?.pais_destino) setPaisDestino(inv.pais_destino);
    if (datos.nombreNave != null) setNombreNave(datos.nombreNave);
    if (datos.puertoOrigen != null) setPuertoOrigen(datos.puertoOrigen);
    if (datos.puertoDestino != null) setPuertoDestino(datos.puertoDestino);
    if (datos.lugarEntrega != null) setLugarEntrega(datos.lugarEntrega);
    if (datos.nombreBl != null) setNombreBl(datos.nombreBl);
    else if (inv?.nombre_bl) setNombreBl(inv.nombre_bl);
    if (datos.contractNo != null) setContractNo(datos.contractNo);
    else if (inv?.contrato_pi) setContractNo(inv.contrato_pi);
    if (datos.idsProformaSeleccion?.length) {
      setIdsProformaSeleccion(datos.idsProformaSeleccion.map((id) => String(id)));
    }
    if (datos.purchaseOrder != null) setPurchaseOrder(datos.purchaseOrder);
    if (datos.netWeight != null && datos.netWeight !== '') setNetWeight(String(datos.netWeight));
    if (datos.grossWeight != null && datos.grossWeight !== '') setGrossWeight(String(datos.grossWeight));
    if (datos.salesTerms != null) setSalesTerms(datos.salesTerms);
    if (datos.incoterms != null) setIncotermsTexto(datos.incoterms);
    if (datos.paymentTermsPtc != null) setPaymentTermsPtc(datos.paymentTermsPtc);
    if (datos.quantity != null) setQuantity(String(datos.quantity));
    if (datos.contractNoPtb != null) setContractNoPtb(datos.contractNoPtb);
    if (datos.purchaseOrderPtb != null) setPurchaseOrderPtb(datos.purchaseOrderPtb);
    if (datos.termsOfSalePtb != null) setTermsOfSalePtb(datos.termsOfSalePtb);
    if (datos.clausePtb != null) setClausePtb(datos.clausePtb);
    if (datos.paymentTermsPtb != null) setPaymentTermsPtb(datos.paymentTermsPtb);
    if (datos.numeroIdentificadorPtc != null) {
      setNumeroIdentificadorPtc(invoiceNumberCorrelativo(datos.numeroIdentificadorPtc));
    }
    if (datos.numeroIdentificadorPtb != null) {
      setNumeroIdentificadorPtb(invoiceNumberCorrelativo(datos.numeroIdentificadorPtb));
    }
    if (datos.hsCodeSnapshot != null) setHsCodeSnapshot(datos.hsCodeSnapshot);
    if (datos.productDescriptionSnapshot != null) {
      setProductDescriptionSnapshot(datos.productDescriptionSnapshot);
    }
    if (datos.invoiceCurrency) setInvoiceCurrency(String(datos.invoiceCurrency).toUpperCase());
    if (datos.totalUsd != null && datos.totalUsd !== '') setValorUsd(String(datos.totalUsd));
    else if (inv?.valor_usd != null) setValorUsd(String(inv.valor_usd));
    if (datos.totalM3 != null && datos.totalM3 !== '') setTotalM3(String(datos.totalM3));
    if (datos.totalBundles != null && datos.totalBundles !== '') {
      setTotalContenedores(String(datos.totalBundles));
    }
    if (Array.isArray(datos.lineasMercancia) && datos.lineasMercancia.length) {
      mercPayloadRef.current = {
        lineasMercancia: datos.lineasMercancia,
        totalBundles: datos.totalBundles,
        totalM3: datos.totalM3,
        totalUsd: datos.totalUsd,
      };
    }
  }, []);

  const cargarInvoiceEdicion = useCallback(async (idInv, proformasLista) => {
      hydratingEditRef.current = true;
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const res = await fetch(`${urlApiProd}/commercialinvoices/${idInv}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.error || 'No se pudo cargar la invoice.');
        hydratingEditRef.current = false;
        return;
      }
      const inv = await res.json();
      const f = inv.datos_formulario || {};
      setIdPackingList(inv.id_packing_list);
      setBookingBusqueda(inv.nro_booking || '');
      setEstadoInvoice(inv.estado || 'prefactura');
      aplicarDatosFormulario(f, inv);
      if (inv.id_cliente != null) setIdCliente(String(inv.id_cliente));
      const plRes = await fetch(`${urlApiProd}/packinglists/${inv.id_packing_list}`, { headers });
      if (plRes.ok) {
        const pl = await plRes.json();
        detalleLineasRef.current = pl.lineas || [];
        if (!f.fechaEmbarque && pl.fecha_embarque) {
          setFechaEmbarque(String(pl.fecha_embarque).slice(0, 10));
        }
        const listaPi = proformasLista?.length ? proformasLista : proformas;
        const pi =
          proformaFromLista(listaPi, f.idsProformaSeleccion || pl.id_proformas || [], f.contractNo) ||
          null;
        idProformaUltimaRef.current = pi;
        const numeroVista =
          inv.estado === 'factura'
            ? invoiceNumberCorrelativo(f.numeroIdentificadorPtc || inv.numero_invoice)
            : numeroParaVistaPrevia(f.numeroIdentificadorPtc, inv.nro_booking);
        aplicarMercanciaDesdePl(pl, pi, numeroVista);
      }
      setErrors({});
      hydratingEditRef.current = false;
    },
    [aplicarDatosFormulario, aplicarMercanciaDesdePl]
  );

  const cargarDesdePackingList = useCallback(
    async (row) => {
      const token = localStorage.getItem('token');
      const res = await fetch(`${urlApiProd}/packinglists/${row.id_packing_list}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err.error || 'No se pudo cargar el packing list.');
        return;
      }
      const pl = await res.json();
      setIdPackingList(pl.id_packing_list);
      setBookingBusqueda(pl.nro_booking || '');
      setFechaEmbarque(pl.fecha_embarque || '');
      setPaisOrigen(pl.pais_origen || '');
      setPaisDestino(pl.pais_destino || '');
      setNombreNave(pl.nombre_nave || '');
      setPuertoOrigen(pl.nombre_puerto_origen || '');
      setPuertoDestino(pl.nombre_puerto_destino || '');
      setLugarEntrega(pl.lugar_entrega || '');
      setNombreBl(pl.nombre_bl || '');
      setClausePtb(`FOB ${puertoNombreCorto(pl.nombre_puerto_origen)}`);
      setIdsProformaSeleccion((pl.id_proformas || []).map((id) => String(id)));

      const lineas = pl.lineas || [];
      const gross = sumPesoGrossByMergeGroups(lineas);
      const net = sumPesoNetByMergeGroups(lineas);
      setGrossWeight(gross ? String(Math.round(gross * 1000) / 1000) : '');
      setNetWeight(net ? String(Math.round(net * 1000) / 1000) : '');
      const tc = pl.total_contenedores;
      const tm = pl.total_volumen_m3;
      setTotalContenedores(tc != null ? String(tc) : '');
      setTotalM3(
        tm != null
          ? String(tm)
          : lineas.length
            ? String(
                Math.round(
                  lineas.reduce((s, l) => s + (Number(l.total_m3) || 0), 0) * 1000
                ) / 1000
              )
            : ''
      );
      setHsCodeSnapshot(pl.hs_code_snapshot || '');
      setProductDescriptionSnapshot(pl.product_description_snapshot || '');

      const cli =
        pl.id_cliente != null
          ? clientes.find((c) => String(c.id_cliente) === String(pl.id_cliente))
          : null;
      if (cli) {
        setIdCliente(String(cli.id_cliente));
        setClienteBusqueda(cli.nombre || '');
        setTaxId(cli.tax ? String(cli.tax).trim() : '');
        setPinCode(cli.codigo_postal ? String(cli.codigo_postal).trim() : '');
        setClienteDireccion(
          buildClienteDireccionTexto(cli, pl.cliente_direccion_snapshot)
        );
      } else {
        setClienteBusqueda(pl.cliente_nombre_snapshot || '');
        setClienteDireccion(pl.cliente_direccion_snapshot || '');
        setPinCode(pl.cliente_codigo_postal ? String(pl.cliente_codigo_postal).trim() : '');
      }

      setNumeroIdentificadorPtc('');
      setNumeroIdentificadorPtb('');

      detalleLineasRef.current = lineas;
      let pi = proformaFromLista(
        proformas,
        (pl.id_proformas || []).map((id) => String(id)),
        ''
      );
      if (pl.id_proformas?.length) {
        if (!pi) {
          const ultima = pl.id_proformas[pl.id_proformas.length - 1];
          pi = proformas.find((p) => String(p.id_proforma) === String(ultima)) || null;
        }
        if (pi && !taxId && pi.cliente) {
          const cPi = clientes.find((c) => String(c.id_cliente) === String(pi.cliente));
          if (cPi?.tax) setTaxId(String(cPi.tax).trim());
        }
      }
      aplicarMercanciaDesdePl(pl, pi, numeroParaVistaPrevia('', pl.nro_booking));
    },
    [clientes, proformas, aplicarMercanciaDesdePl]
  );

  const elegirBooking = (row) => {
    bookingElegidoRecienteRef.current = true;
    setBookingMenuOpen(false);
    setSaveError('');
    cargarDesdePackingList(row);
    window.setTimeout(() => {
      bookingElegidoRecienteRef.current = false;
    }, 400);
  };

  const onSelectCliente = (id) => {
    setIdCliente(id);
    const c = clientes.find((x) => String(x.id_cliente) === String(id));
    if (c) {
      setClienteBusqueda(c.nombre || '');
      setTaxId(c.tax ? String(c.tax).trim() : '');
      setPinCode(c.codigo_postal ? String(c.codigo_postal).trim() : '');
      setClienteDireccion(buildClienteDireccionTexto(c, ''));
      setPaisDestino(c.pais || '');
      setLugarEntrega(c.direccion_despacho || lugarEntrega);
    }
  };

  useEffect(() => {
    if (show) {
      setShowGuardadoToast(false);
      setGuardadoToastMensaje('');
    }
  }, [show]);

  useEffect(() => {
    setIdsProformaSeleccion((prev) =>
      prev.filter((id) =>
        proformasFiltradas.some((p) => String(p.id_proforma) === String(id))
      )
    );
  }, [idCliente, proformasFiltradas]);

  useEffect(() => {
    if (hydratingEditRef.current) return;
    const label = getPiLabel(idProformaUltima, proformas);
    if (idProformaUltima && label) {
      setContractNo(label);
    } else if (!idsProformaSeleccion.length) {
      setContractNo('');
    }
    const pi = proformaFromLista(proformas, idsProformaSeleccion, contractNo);
    idProformaUltimaRef.current = pi;
    if (pi) {
      setIncotermsTexto(incotermDesdePi(pi, incoterms, puertos));
      setPaymentTermsPtc(paymentDesdePi(pi, paymentTerms));
    } else if (!idsProformaSeleccion.length) {
      setIncotermsTexto('');
      setPaymentTermsPtc('');
    }
    if (detalleLineasRef.current?.length) {
      aplicarMercanciaDesdePl(
        { lineas: detalleLineasRef.current, fecha_embarque: fechaEmbarque },
        pi
      );
    }
  }, [
    contractNo,
    idsProformaSeleccion,
    proformas,
    incoterms,
    paymentTerms,
    puertos,
    fechaEmbarque,
    aplicarMercanciaDesdePl,
  ]);

  const onProformaBusquedaChange = (e) => {
    setProformaBusqueda(e.target.value);
    setProformaMenuOpen(true);
  };

  const elegirProformaLista = (pi) => {
    proformaElegidaRecienteRef.current = true;
    setIdsProformaSeleccion((prev) => appendProformaIdOrdenado(prev, pi.id_proforma));
    setProformaBusqueda('');
    setProformaMenuOpen(true);
    window.setTimeout(() => {
      proformaInputRef.current?.focus();
    }, 0);
    window.setTimeout(() => {
      proformaElegidaRecienteRef.current = false;
    }, 400);
  };

  const quitarProformaChip = (idProforma) => {
    setIdsProformaSeleccion((prev) => prev.filter((x) => String(x) !== String(idProforma)));
  };

  const onProformaInputBlur = () => {
    window.setTimeout(() => {
      if (proformaElegidaRecienteRef.current) {
        setProformaMenuOpen(true);
        return;
      }
      setProformaMenuOpen(false);
    }, 180);
  };

  useEffect(() => {
    if (!show) {
      invoiceModalLoadKeyRef.current = null;
      hydratingEditRef.current = false;
      return undefined;
    }

    const loadKey = String(invoiceEditId ?? 'new');
    if (invoiceModalLoadKeyRef.current === loadKey) return undefined;
    invoiceModalLoadKeyRef.current = loadKey;

    let cancelled = false;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    setLoading(true);
    setSaveError('');

    (async () => {
      let proformasCargadas = [];
      try {
        const [r1, r2, r3, r4, r5, rPl, rInv] = await Promise.all([
          fetch(`${urlApiProd}/clientes`, { headers }),
          fetch(`${urlApiProd}/proformas`, { headers }),
          fetch(`${urlApiProd}/incoterms`, { headers }),
          fetch(`${urlApiProd}/paymentterms`, { headers }),
          fetch(`${urlApiProd}/puertos`, { headers }),
          fetch(`${urlApiProd}/packinglists`, { headers }),
          fetch(`${urlApiProd}/commercialinvoices`, { headers }),
        ]);
        if (cancelled) return;
        if (r1.ok) setClientes(await r1.json());
        if (r2.ok) {
          proformasCargadas = await r2.json();
          setProformas(proformasCargadas);
        }
        if (r3.ok) setIncoterms(await r3.json());
        if (r4.ok) setPaymentTerms(await r4.json());
        if (r5.ok) setPuertos(await r5.json());
        const pl = rPl.ok ? await rPl.json() : [];
        const inv = rInv.ok ? await rInv.json() : [];
        const disponibles = packingListsSinInvoice(
          Array.isArray(pl) ? pl : [],
          Array.isArray(inv) ? inv : []
        );
        if (disponibles.length) {
          setPackingDisponiblesLocal(disponibles);
        } else if (Array.isArray(packingListDisponibles) && packingListDisponibles.length) {
          setPackingDisponiblesLocal(packingListDisponibles);
        } else {
          setPackingDisponiblesLocal([]);
        }
        if (invoiceEditId) {
          await cargarInvoiceEdicion(invoiceEditId, proformasCargadas);
        } else {
          resetForm();
        }
      } catch (e) {
        if (!cancelled) console.error('Error cargando datos invoice:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- una carga por apertura (loadKey)
  }, [show, invoiceEditId]);

  useEffect(() => {
    if (hydratingEditRef.current || loading) return;
    if (!detalleLineasRef.current?.length) return;
    aplicarMercanciaDesdePl(
      { lineas: detalleLineasRef.current, fecha_embarque: fechaEmbarque },
      idProformaUltimaRef.current
    );
  }, [invoiceCurrency, aplicarMercanciaDesdePl, fechaEmbarque, loading]);

  const validate = () => {
    const e = {};
    if (!idPackingList && !bookingBusqueda.trim()) {
      e.booking = 'Seleccione un N° Booking con packing list.';
    }
    if (!fechaEmbarque) e.fechaEmbarque = 'Indique fecha de embarque.';
    if (!clienteBusqueda.trim()) e.cliente = 'Indique el cliente.';
    if (!nombreBl.trim()) e.nombreBl = 'Ingrese el nombre del BL.';
    if (netWeight === '' || netWeight == null) e.netWeight = 'Net Weight requerido (desde booking).';
    if (grossWeight === '' || grossWeight == null) e.grossWeight = 'Gross Weight requerido (desde booking).';
    if (!fechaInvoice) e.fechaInvoice = 'Indique fecha de invoice.';
    if (valorUsd === '' || valorUsd == null || Number.isNaN(Number(valorUsd))) {
      e.valorUsd = 'Indique el valor total en USD.';
    }
    if (!isEditMode && !idPackingList) {
      e.booking = 'Seleccione un booking con packing list sin invoice.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildFormPayload = () => ({
    empresaEmisor,
    fechaEmbarque,
    fechaInvoice,
    clienteNombre: clienteBusqueda,
    clienteDireccion,
    taxId,
    pinCode,
    paisOrigen,
    paisDestino,
    nombreNave,
    puertoOrigen,
    puertoDestino,
    lugarEntrega,
    nombreBl,
    contractNo,
    idsProformaSeleccion,
    purchaseOrder,
    netWeight: parseFloat(netWeight),
    grossWeight: parseFloat(grossWeight),
    salesTerms,
    incoterms: incotermsTexto,
    paymentTermsPtc,
    quantity,
    contractNoPtb,
    purchaseOrderPtb,
    termsOfSalePtb,
    clausePtb,
    paymentTermsPtb,
    numeroIdentificadorPtc,
    numeroIdentificadorPtb,
    textoEmpresaPtc,
    textoEmpresaPtb,
    hsCodeSnapshot,
    productDescriptionSnapshot,
    invoiceCurrency: (invoiceCurrency || 'USD').trim().toUpperCase(),
    lineasMercancia: mercPayloadRef.current?.lineasMercancia || [],
    totalBundles: mercPayloadRef.current?.totalBundles,
    totalM3: mercPayloadRef.current?.totalM3,
    totalUsd: mercPayloadRef.current?.totalUsd,
  });

  const cerrarPreview = useCallback(() => {
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant('PTC');
    setShowPreview(false);
    setPreviewError('');
    previewParamsRef.current = null;
  }, [revokePreviewUrls]);

  const validatePreview = () => {
    const e = {};
    if (!idPackingList) e.booking = 'Seleccione un booking con packing list.';
    if (!clienteBusqueda.trim()) e.cliente = 'Indique el cliente.';
    if (!fechaInvoice) e.fechaInvoice = 'Indique fecha de invoice.';
    if (!detalleLineasRef.current?.length) {
      e.detalle = 'El packing list no tiene líneas de detalle.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resolverPiParaPrecios = useCallback(() => {
    return (
      idProformaUltimaRef.current ||
      proformaFromLista(proformas, idsProformaSeleccion, contractNo) ||
      null
    );
  }, [proformas, idsProformaSeleccion, contractNo]);

  const buildPtbPreviewParams = async (
    datosFormulario,
    { numeroInvoice, valorUsd, piResolved, headers }
  ) => {
    const overridesPtb = { numeroInvoice, valorUsd };
    if (!idPackingList || !headers) {
      return invoicePtbParamsFromFormAndDetail(
        datosFormulario,
        detalleLineasRef.current,
        piResolved,
        overridesPtb
      );
    }
    return invoicePtbParamsFromFormWithPackingList(
      datosFormulario,
      overridesPtb,
      {
        apiBase: urlApiProd,
        headers,
        idPackingList,
        proformaEnMemoria: piResolved,
        plLineas: detalleLineasRef.current,
      }
    );
  };

  const handleVistaPrevia = async () => {
    setPreviewError('');
    if (!validatePreview()) return;
    const esPtb = empresaEmisor === EMPRESA_PTB;
    const numeroPreview = numeroParaVistaPrevia(
      esPtb ? numeroIdentificadorPtb : numeroIdentificadorPtc,
      bookingBusqueda
    );
    const piResolved = resolverPiParaPrecios();
    if (!piResolved) {
      setPreviewError(
        'Seleccione la PI (Contract No.) en el formulario. Los precios UNIT PRICE / us$/M³ salen de la PI vinculada.'
      );
      return;
    }
    if (detalleLineasRef.current?.length) {
      aplicarMercanciaDesdePl(
        {
          lineas: detalleLineasRef.current,
          fecha_embarque: fechaEmbarque,
          product_description_snapshot: productDescriptionSnapshot,
        },
        piResolved,
        numeroPreview
      );
    }
    setPreviewLoading(true);
    setShowPreview(true);
    setPreviewUrls((prev) => {
      revokePreviewUrls(prev);
      return { PTC: null, PTB: null };
    });
    setPreviewVariant(esPtb ? 'PTB' : 'PTC');
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : null;
    try {
      const datosFormulario = buildFormPayload();
      const valor =
        mercPayloadRef.current?.totalUsd > 0
          ? mercPayloadRef.current.totalUsd
          : parseFloat(valorUsd);
      const valorOpt = Number.isFinite(valor) ? valor : null;
      if (esPtb) {
        const paramsPtb = await buildPtbPreviewParams(datosFormulario, {
          numeroInvoice: numeroIdentificadorPtb.trim(),
          valorUsd: valorOpt,
          piResolved,
          headers,
        });
        if (!lineasPdfTienenPrecios(paramsPtb?.lineas)) {
          setPreviewError(
            'La PI del Contract No. no tiene USD/m³ (ni USD total por línea) para cruzar con el packing list. Abra la PI en Proformas y complete USD/m³ o USD total y M³ por producto.'
          );
        }
        previewParamsRef.current = { PTC: null, PTB: paramsPtb };
        setPreviewUrls({
          PTC: null,
          PTB: createInvoicePtbPdfBlobUrl(paramsPtb),
        });
      } else {
        const overridesPtc = {
          numeroInvoice: numeroIdentificadorPtc.trim(),
          valorUsd: valorOpt,
        };
        let paramsPtc = invoicePtcParamsFromFormAndDetail(
          datosFormulario,
          detalleLineasRef.current,
          piResolved,
          overridesPtc
        );
        if (idPackingList && headers) {
          paramsPtc = await invoicePtcParamsFromFormWithPackingList(
            datosFormulario,
            overridesPtc,
            {
              apiBase: urlApiProd,
              headers,
              idPackingList,
              proformaEnMemoria: piResolved,
              plLineas: detalleLineasRef.current,
            }
          );
        }
        if (!lineasPdfTienenPrecios(paramsPtc?.lineas)) {
          setPreviewError(
            'La PI del Contract No. no tiene USD/m³ (ni USD total por línea) para cruzar con el packing list. Abra la PI en Proformas y complete USD/m³ o USD total y M³ por producto.'
          );
        }
        const paramsPtb = await buildPtbPreviewParams(datosFormulario, {
          numeroInvoice: numeroIdentificadorPtb.trim(),
          valorUsd: valorOpt,
          piResolved,
          headers,
        });
        previewParamsRef.current = { PTC: paramsPtc, PTB: paramsPtb };
        setPreviewUrls({
          PTC: createInvoicePtcPdfBlobUrl(paramsPtc),
          PTB: createInvoicePtbPdfBlobUrl(paramsPtb),
        });
      }
    } catch (err) {
      setPreviewError(err.message || 'No se pudo generar la vista previa.');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDescargarPreview = () => {
    const ref = previewParamsRef.current;
    if (!ref) return;
    if (previewVariant === 'PTB') {
      if (ref.PTB) generateInvoicePtbPdf(ref.PTB);
    } else {
      if (ref.PTC) generateInvoicePtcPdf(ref.PTC);
    }
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSaveError('');
    setSaving(true);
    const identificadorForm = empresaEmisor === EMPRESA_PTB ? numeroIdentificadorPtb : numeroIdentificadorPtc;
    const numeroVista = numeroParaVistaPrevia(identificadorForm, bookingBusqueda);
    const numeroGuardar =
      estadoInvoice === 'factura'
        ? invoiceNumberCorrelativo(identificadorForm)
        : '';
    if (detalleLineasRef.current?.length) {
      aplicarMercanciaDesdePl(
        {
          lineas: detalleLineasRef.current,
          fecha_embarque: fechaEmbarque,
        },
        idProformaUltimaRef.current,
        numeroVista
      );
    }
    const datosFormulario = buildFormPayload();
    try {
      const token = localStorage.getItem('token');
      const payloadBase = {
        numero_invoice: numeroGuardar,
        fecha_invoice: fechaInvoice,
        empresa_emisor: empresaEmisor,
        nombre_bl: nombreBl.trim(),
        nombre_cliente: clienteBusqueda.trim(),
        pais_destino: paisDestino,
        valor_usd:
          mercPayloadRef.current?.totalUsd > 0
            ? mercPayloadRef.current.totalUsd
            : parseFloat(valorUsd),
        contrato_pi: contractNo.trim(),
        total_contenedores: totalContenedores === '' ? null : parseFloat(totalContenedores),
        total_m3: totalM3 === '' ? null : parseFloat(totalM3),
        datos_formulario: datosFormulario,
      };
      const url = isEditMode
        ? `${urlApiProd}/commercialinvoices/${invoiceEditId}`
        : `${urlApiProd}/commercialinvoices`;
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          isEditMode
            ? payloadBase
            : {
                ...payloadBase,
                id_packing_list: idPackingList,
                fecha_embarque: fechaEmbarque,
                id_cliente: idCliente ? parseInt(idCliente, 10) : null,
              }
        ),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(body.error || `Error ${res.status}`);
        return;
      }
      setGuardadoToastMensaje(
        isEditMode ? 'Invoice editada correctamente' : 'Prefactura creada exitosamente'
      );
      setShowGuardadoToast(true);
      if (typeof onGuardarExito === 'function') onGuardarExito();
      cerrar();
    } catch (e) {
      setSaveError(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const mostrarBloquePtb = empresaEmisor === EMPRESA_PTC;
  const previewUrlActual = previewVariant === 'PTB' ? previewUrls.PTB : previewUrls.PTC;
  const previewTitle =
    empresaEmisor === EMPRESA_PTB
      ? 'Vista previa — Invoice PTB'
      : `Vista previa — Invoice ${previewVariant}`;
  const previewIframeTitle =
    empresaEmisor === EMPRESA_PTB
      ? 'Vista previa Invoice PTB'
      : `Vista previa Invoice ${previewVariant}`;

  return (
    <>
    <ToastContainer
      className="p-3"
      position="top-center"
      style={{ zIndex: 99999, maxWidth: 'unset' }}
    >
      <Toast
        style={{ width: 'min(35vw, 420px)' }}
        onClose={() => setShowGuardadoToast(false)}
        show={showGuardadoToast}
        delay={3500}
        autohide
      >
        <Toast.Header>
          <strong className="me-auto">Mensaje del sistema</strong>
        </Toast.Header>
        <Toast.Body className="text-center">{guardadoToastMensaje}</Toast.Body>
      </Toast>
    </ToastContainer>
    <ElegantModal
      show={show}
      onClose={cerrar}
      dismissable={false}
      title={isEditMode ? 'Editar invoice' : 'Crear Invoice'}
      subtitle={
        isEditMode
          ? 'Modifique los datos de la prefactura/factura. El booking no se puede cambiar.'
          : 'Seleccione empresa y booking con packing list. Los campos se completan desde el booking y pueden editarse.'
      }
      titleId="invoice-create-title"
      size="xl"
      footer={
        loading ? null : (
          <div className="d-flex gap-2 justify-content-end flex-wrap">
            <Button variant="secondary" type="button" onClick={cerrar} disabled={saving || previewLoading}>
              Cancelar
            </Button>
            <Button
              variant="outline-primary"
              type="button"
              onClick={handleVistaPrevia}
              disabled={saving || previewLoading}
            >
              {previewLoading ? 'Generando…' : 'Vista previa'}
            </Button>
            <Button variant="primary" type="submit" form="invoice-form-add" disabled={saving || previewLoading}>
              {saving ? 'Guardando…' : isEditMode ? 'Guardar cambios' : 'Guardar prefactura'}
            </Button>
          </div>
        )
      }
    >
      <form id="invoice-form-add" onSubmit={handleSubmit}>
        {loading ? (
          <p className="text-center text-muted mb-0">Cargando datos…</p>
        ) : (
          <>
            {saveError ? (
              <Alert variant="danger" className="mb-3" dismissible onClose={() => setSaveError('')}>
                {saveError}
              </Alert>
            ) : null}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Empresa emisora</Form.Label>
                  <div className="d-flex flex-wrap gap-3">
                    <Form.Check
                      type="radio"
                      id="inv-empresa-ptc"
                      name="empresaInvoice"
                      label="PTC — Guarda Invoice PTC + Invoice PTB"
                      checked={empresaEmisor === EMPRESA_PTC}
                      onChange={() => setEmpresaEmisor(EMPRESA_PTC)}
                    />
                    <Form.Check
                      type="radio"
                      id="inv-empresa-ptb"
                      name="empresaInvoice"
                      label="PTB — Guarda solo Invoice PTB (campos PTC)"
                      checked={empresaEmisor === EMPRESA_PTB}
                      onChange={() => setEmpresaEmisor(EMPRESA_PTB)}
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <div ref={bookingAnchorRef} className="position-relative">
                    <Form.Label>N° Booking</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="off"
                      readOnly={isEditMode}
                      placeholder={
                        isEditMode ? 'Booking vinculado' : 'Buscar booking con packing list…'
                      }
                      value={bookingBusqueda}
                      onChange={(e) => {
                        if (isEditMode) return;
                        setBookingBusqueda(e.target.value);
                        setBookingMenuOpen(true);
                        setIdPackingList(null);
                      }}
                      onFocus={() => {
                        if (!isEditMode) setBookingMenuOpen(true);
                      }}
                      onBlur={() => {
                        window.setTimeout(() => {
                          if (!bookingElegidoRecienteRef.current) setBookingMenuOpen(false);
                        }, 180);
                      }}
                      isInvalid={!!errors.booking}
                    />
                    <BookingAutocompletePortal
                      open={!isEditMode && bookingMenuOpen}
                      anchorRef={bookingAnchorRef}
                    >
                      {bookingsOpciones.length > 0 ? (
                        bookingsOpciones.map((r) => (
                          <ListGroup.Item
                            key={r.id_packing_list}
                            action
                            className="py-2 px-3"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              elegirBooking(r);
                            }}
                          >
                            {r.nro_booking}
                          </ListGroup.Item>
                        ))
                      ) : (
                        <ListGroup.Item disabled className="py-2 px-3 small text-muted">
                          {packingDisponiblesLista.length === 0
                            ? 'No hay packing lists sin invoice'
                            : 'Sin coincidencias para la búsqueda'}
                        </ListGroup.Item>
                      )}
                    </BookingAutocompletePortal>
                    <Form.Text className="text-muted">
                      Solo packing lists sin invoice asociada.
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">{errors.booking}</Form.Control.Feedback>
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Fecha embarque</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaEmbarque}
                    onChange={(e) => setFechaEmbarque(e.target.value)}
                    isInvalid={!!errors.fechaEmbarque}
                  />
                  <Form.Control.Feedback type="invalid">{errors.fechaEmbarque}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Fecha Invoice</Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaInvoice}
                    onChange={(e) => setFechaInvoice(e.target.value)}
                    isInvalid={!!errors.fechaInvoice}
                  />
                  <Form.Control.Feedback type="invalid">{errors.fechaInvoice}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Moneda invoice (Currency)</Form.Label>
                  <Form.Control
                    value={invoiceCurrency}
                    onChange={(e) => setInvoiceCurrency(e.target.value.toUpperCase())}
                    placeholder="USD"
                    maxLength={8}
                  />
                  <Form.Text className="text-muted">Por defecto USD</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Valor total USD</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={valorUsd}
                    onChange={(e) => setValorUsd(e.target.value)}
                    isInvalid={!!errors.valorUsd}
                  />
                  <Form.Control.Feedback type="invalid">{errors.valorUsd}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group className="position-relative">
                        <Form.Label>Cliente / Customer (nombre del cliente)</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="off"
                    value={clienteBusqueda}
                    onChange={(e) => {
                      setClienteBusqueda(e.target.value);
                      setClienteMenuOpen(true);
                    }}
                    onFocus={() => setClienteMenuOpen(true)}
                    onBlur={() => window.setTimeout(() => setClienteMenuOpen(false), 180)}
                    isInvalid={!!errors.cliente}
                  />
                  {clienteMenuOpen && clientesFiltrados.length > 0 ? (
                    <ListGroup
                      className="position-absolute w-100 shadow border rounded overflow-auto"
                      style={{ zIndex: 5, top: '100%', marginTop: 2, maxHeight: 220 }}
                    >
                      {clientesFiltrados.map((c) => (
                        <ListGroup.Item
                          key={c.id_cliente}
                          action
                          className="py-2 px-3"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            onSelectCliente(String(c.id_cliente));
                            setClienteMenuOpen(false);
                          }}
                        >
                          {c.nombre}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : null}
                  <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>TAX ID (PI / cliente)</Form.Label>
                  <Form.Control value={taxId} onChange={(e) => setTaxId(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>PIN CODE</Form.Label>
                  <Form.Control value={pinCode} onChange={(e) => setPinCode(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Dirección cliente (encabezado PDF)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={clienteDireccion}
                    onChange={(e) => setClienteDireccion(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>País origen / Country of origin</Form.Label>
                  <Form.Control value={paisOrigen} onChange={(e) => setPaisOrigen(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>País destino / Destination Country</Form.Label>
                  <Form.Control value={paisDestino} onChange={(e) => setPaisDestino(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Nombre nave / Vessel name</Form.Label>
                  <Form.Control value={nombreNave} onChange={(e) => setNombreNave(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Puerto origen / Loading Port</Form.Label>
                  <Form.Control value={puertoOrigen} onChange={(e) => setPuertoOrigen(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Puerto destino / Unloading Port</Form.Label>
                  <Form.Control value={puertoDestino} onChange={(e) => setPuertoDestino(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            {(empresaEmisor === EMPRESA_PTC || empresaEmisor === EMPRESA_PTB) && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Lugar de entrega / Place of delivery</Form.Label>
                    <Form.Control value={lugarEntrega} onChange={(e) => setLugarEntrega(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nombre BL</Form.Label>
                  <Form.Control
                    value={nombreBl}
                    onChange={(e) => setNombreBl(e.target.value)}
                    isInvalid={!!errors.nombreBl}
                  />
                  <Form.Control.Feedback type="invalid">{errors.nombreBl}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="position-relative">
                  <Form.Label>N° de contrato / Contract No.</Form.Label>
                  {idsProformaSeleccion.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {idsProformaSeleccion.map((idStr) => {
                        const pi = proformasFiltradas.find(
                          (p) => String(p.id_proforma) === String(idStr)
                        );
                        const lbl = pi != null ? proformaEtiqueta(pi) : `id ${idStr}`;
                        return (
                          <Badge
                            key={String(idStr)}
                            bg="secondary"
                            className="d-inline-flex align-items-center gap-1 fw-normal py-1 px-2"
                          >
                            <span>{lbl}</span>
                            <Button
                              type="button"
                              variant="link"
                              className="text-white text-decoration-none p-0 lh-1"
                              style={{ fontSize: '1rem', minWidth: '1rem' }}
                              onClick={() => quitarProformaChip(idStr)}
                              aria-label={`Quitar ${lbl}`}
                            >
                              ×
                            </Button>
                          </Badge>
                        );
                      })}
                    </div>
                  ) : null}
                  <Form.Control
                    ref={proformaInputRef}
                    type="text"
                    autoComplete="off"
                    value={proformaBusqueda}
                    onChange={onProformaBusquedaChange}
                    onFocus={() => setProformaMenuOpen(true)}
                    onBlur={onProformaInputBlur}
                    placeholder="Buscar y agregar PI (número o correlativo)…"
                  />
                  {proformaMenuOpen && proformasBusquedaLista.length > 0 ? (
                    <ListGroup
                      className="position-absolute w-100 shadow border rounded overflow-auto"
                      style={{
                        zIndex: 5,
                        top: '100%',
                        marginTop: 2,
                        maxHeight: 220,
                      }}
                    >
                      {proformasBusquedaLista.map((pi) => (
                        <ListGroup.Item
                          key={pi.id_proforma}
                          action
                          className="py-2 px-3"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            elegirProformaLista(pi);
                          }}
                        >
                          {proformaEtiqueta(pi)}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : null}

                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>N° pedido compra / Purchase Order</Form.Label>
                  <Form.Control
                    value={purchaseOrder}
                    onChange={(e) => setPurchaseOrder(e.target.value)}
                    placeholder={empresaEmisor === EMPRESA_PTB ? 'Opcional' : ''}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Net Weight (Kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={netWeight}
                    onChange={(e) => setNetWeight(e.target.value)}
                    isInvalid={!!errors.netWeight}
                  />
                  <Form.Control.Feedback type="invalid">{errors.netWeight}</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Gross Weight (Kg)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    value={grossWeight}
                    onChange={(e) => setGrossWeight(e.target.value)}
                    isInvalid={!!errors.grossWeight}
                  />
                  <Form.Control.Feedback type="invalid">{errors.grossWeight}</Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Sales terms</Form.Label>
                  <Form.Control value={salesTerms} onChange={(e) => setSalesTerms(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Incoterms (Sales condition PI)</Form.Label>
                  <Form.Control value={incotermsTexto} onChange={(e) => setIncotermsTexto(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Payment terms (PI)</Form.Label>
                  <Form.Control value={paymentTermsPtc} onChange={(e) => setPaymentTermsPtc(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Quantity (total M³ PL)</Form.Label>
                  <Form.Control
                    type="number"
                    step="any"
                    min="0"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    placeholder="Total M³ del packing list"
                  />
                </Form.Group>
              </Col>
              {empresaEmisor === EMPRESA_PTC ? (
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>N° identificador PTC</Form.Label>
                    <Form.Control
                      value={numeroIdentificadorPtc}
                      onChange={(e) => setNumeroIdentificadorPtc(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              ) : null}
              <Col md={empresaEmisor === EMPRESA_PTB ? 6 : 3}>
                <Form.Group>
                  <Form.Label>N° identificador PTB</Form.Label>
                  <Form.Control
                    value={numeroIdentificadorPtb}
                    onChange={(e) => setNumeroIdentificadorPtb(e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {mostrarBloquePtb ? (
              <>
                <hr />
                <h6 className="mb-3 text-muted">Datos Invoice PTB (segunda impresión — logo PTB)</h6>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Contract No. (PTB)</Form.Label>
                      <Form.Control
                        value={contractNoPtb}
                        onChange={(e) => setContractNoPtb(e.target.value)}
                        placeholder="Por defecto vacío"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Purchase Order (PTB)</Form.Label>
                      <Form.Control
                        value={purchaseOrderPtb}
                        onChange={(e) => setPurchaseOrderPtb(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Terms of Sale (PTB)</Form.Label>
                      <Form.Control
                        value={termsOfSalePtb}
                        onChange={(e) => setTermsOfSalePtb(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Clause (PTB)</Form.Label>
                      <Form.Control value={clausePtb} onChange={(e) => setClausePtb(e.target.value)} />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Payment terms (PTB)</Form.Label>
                      <Form.Control
                        value={paymentTermsPtb}
                        onChange={(e) => setPaymentTermsPtb(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : null}
          </>
        )}
      </form>
    </ElegantModal>
    <InvoicePtcPreviewModal
      show={show && showPreview}
      pdfUrl={previewUrlActual}
      loading={previewLoading}
      error={previewError}
      onClose={cerrarPreview}
      onDownload={handleDescargarPreview}
      title={previewTitle}
      iframeTitle={previewIframeTitle}
      showVariantSwitch={empresaEmisor === EMPRESA_PTC}
      selectedVariant={previewVariant}
      onSelectVariant={setPreviewVariant}
    />
    </>
  );
};

export default AgregarInvoice;
